import { useEffect, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Group, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Table, Text, TextInput, Textarea, Title, Tooltip } from '@mantine/core';
import { IconCalendarPlus, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import api from '../api/axiosConfig';

const statusLabels = { PENDING: 'Onay Bekliyor', APPROVED: 'Onaylandı', REJECTED: 'Reddedildi', CANCELLED: 'İptal Edildi' };
const statusColors = { PENDING: 'yellow', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'gray' };

export default function ProfileLeaveCard({ hasEmployee = true }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ leaveTypeId: null, startDate: '', endDate: '', reason: '' });

  const loadData = async () => {
    if (!hasEmployee) return;
    try {
      setLoading(true); setError('');
      const [typeResponse, requestResponse] = await Promise.all([api.get('/profile/leave-types'), api.get('/profile/leave-requests')]);
      setLeaveTypes(typeResponse.data || []); setRequests(requestResponse.data || []);
    } catch (err) { setError(err.response?.data?.detail || 'İzin bilgileri alınamadı.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadData(); }, [hasEmployee]);

  const totalDays = form.startDate && form.endDate && !dayjs(form.endDate).isBefore(dayjs(form.startDate), 'day') ? dayjs(form.endDate).diff(dayjs(form.startDate), 'day') + 1 : 0;
  const returnDate = totalDays ? dayjs(form.endDate).add(1, 'day').format('YYYY-MM-DD') : '';
  const closeModal = () => { setOpened(false); setForm({ leaveTypeId: null, startDate: '', endDate: '', reason: '' }); };

const submit = async () => {
  try {
    setError('');

    const response = await api.post('/profile/leave-requests', {
      leave_type_id: Number(form.leaveTypeId),
      start_date: form.startDate,
      end_date: form.endDate,
      reason: form.reason.trim() || null,
    });

    const createdRequest = response.data;

    // Başarılı talebi beklemeden ekranda göster
    setRequests((current) => [
      createdRequest,
      ...current.filter((item) => item.id !== createdRequest.id),
    ]);

    notifications.show({
      title: 'Talep oluşturuldu',
      message: 'İzin talebiniz İnsan Kaynakları onayına gönderildi.',
      color: 'teal',
    });

    closeModal();

    // Ardından backend ile tekrar senkronize et
    try {
      const requestResponse = await api.get('/profile/leave-requests');

      setRequests(
        Array.isArray(requestResponse.data)
          ? requestResponse.data
          : []
      );
    } catch (refreshError) {
      console.error(
        'İzin talepleri yeniden alınamadı:',
        refreshError
      );
    }
  } catch (err) {
    notifications.show({
      title: 'İzin talebi oluşturulamadı',
      message:
        err.response?.data?.detail ||
        'İşlem sırasında hata oluştu.',
      color: 'red',
    });
  }
};

  const cancel = async (id) => {
    try {
      await api.post(`/profile/leave-requests/${id}/cancel`);
      notifications.show({ title: 'Talep iptal edildi', message: 'İzin talebiniz iptal edildi.', color: 'teal' }); await loadData();
    } catch (err) { notifications.show({ title: 'İptal edilemedi', message: err.response?.data?.detail || 'İşlem sırasında hata oluştu.', color: 'red' }); }
  };

  if (!hasEmployee) return <Paper withBorder radius="xl" p="lg"><Alert color="yellow" title="Personel kaydı bağlı değil">İzin talebi oluşturabilmeniz için portal hesabınızın İK personel kaydınızla eşleştirilmesi gerekiyor.</Alert></Paper>;

  return <Paper withBorder radius="xl" p="md" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
    <Stack gap="md">
      <Group justify="space-between"><div><Title order={4} fw={800}>İzin İşlemlerim</Title><Text size="sm" c="dimmed">İzin talebi oluşturun ve İK onay durumunu takip edin.</Text></div><Button leftSection={<IconCalendarPlus size={17} />} onClick={() => setOpened(true)}>Yeni İzin Talebi</Button></Group>
      {error && <Alert color="red">{String(error)}</Alert>}
      <Table.ScrollContainer minWidth={650}><Table striped highlightOnHover><Table.Thead bg="gray.0"><Table.Tr><Table.Th>İzin Türü</Table.Th><Table.Th>Tarih Aralığı</Table.Th><Table.Th>Gün</Table.Th><Table.Th>Durum</Table.Th><Table.Th>İK Notu</Table.Th><Table.Th ta="right">İşlem</Table.Th></Table.Tr></Table.Thead><Table.Tbody>
        {requests.map((item) => <Table.Tr key={item.id}><Table.Td fw={600}>{item.leave_type_name}</Table.Td><Table.Td>{dayjs(item.start_date).format('DD.MM.YYYY')} – {dayjs(item.end_date).format('DD.MM.YYYY')}</Table.Td><Table.Td>{item.total_days}</Table.Td><Table.Td><Badge color={statusColors[item.status] || 'gray'} variant="light">{statusLabels[item.status] || item.status}</Badge></Table.Td><Table.Td><Text size="sm" c={item.rejection_reason ? 'red' : 'dimmed'}>{item.rejection_reason || '-'}</Text></Table.Td><Table.Td ta="right">{item.status === 'PENDING' && <Tooltip label="Talebi İptal Et"><ActionIcon color="red" variant="light" onClick={() => cancel(item.id)}><IconX size={17} /></ActionIcon></Tooltip>}</Table.Td></Table.Tr>)}
        {!loading && !requests.length && <Table.Tr><Table.Td colSpan={6}><Text ta="center" c="dimmed" py="lg">Henüz izin talebiniz bulunmuyor.</Text></Table.Td></Table.Tr>}
      </Table.Tbody></Table></Table.ScrollContainer>
    </Stack>
    <Modal opened={opened} onClose={closeModal} title="Yeni İzin Talebi" size="lg" centered><Stack>
      <Select required label="İzin Türü" placeholder="İzin türünü seçin" data={leaveTypes.map((item) => ({ value: String(item.id), label: item.name }))} value={form.leaveTypeId} onChange={(value) => setForm((current) => ({ ...current, leaveTypeId: value }))} />
      <SimpleGrid cols={{ base: 1, sm: 2 }}><TextInput required label="Başlangıç" type="date" value={form.startDate} onChange={(event) => { const value = event.currentTarget.value; setForm((current) => ({ ...current, startDate: value })); }} /><TextInput required label="Bitiş" type="date" min={form.startDate || undefined} value={form.endDate} onChange={(event) => { const value = event.currentTarget.value; setForm((current) => ({ ...current, endDate: value })); }} /></SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}><NumberInput label="İzin Süresi" value={totalDays} readOnly suffix=" gün" /><TextInput label="İşe Dönüş" type="date" value={returnDate} readOnly /></SimpleGrid>
      <Textarea label="Açıklama / Gerekçe" minRows={3} value={form.reason} onChange={(event) => { const value = event.currentTarget.value; setForm((current) => ({ ...current, reason: value })); }} />
      <Group justify="flex-end"><Button variant="default" onClick={closeModal}>Vazgeç</Button><Button disabled={!form.leaveTypeId || !totalDays} onClick={submit}>İK'ya Gönder</Button></Group>
    </Stack></Modal>
  </Paper>;
}
